import { Link } from "react-router-dom";

const HERO_GFX = "/brand/orb.png";
const FLAGSHIP_IMG = "/brand/britannia_studio.jpg";
const BRITANNIA_LOGO = "/brand/britannia_white.png";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero — the brand's own vertical composition */}
      <section className="relative min-h-[92vh] flex items-center pt-32 pb-16 overflow-hidden">
        <div className="hm-container relative w-full">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7 relative z-10">
              <h1
                className="font-display leading-[0.94] tracking-[-0.02em] hm-fade-up hm-fade-up-d1"
                style={{ fontSize: "clamp(46px, 6.4vw, 88px)", fontWeight: 400 }}
              >
                Restoring buildings with a past into places with a future.
              </h1>
              <p className="mt-10 text-[color:var(--hm-ink-dim)] max-w-lg text-[17px] leading-relaxed hm-fade-up hm-fade-up-d2">
                Har-Monic is a London-based regenerative property development company. We restore heritage and vacant buildings into design-led residential, hospitality, and creative workspace projects — at the intersection of architecture, culture, and place-making.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4 hm-fade-up hm-fade-up-d3">
                <Link to="/projects/35-britannia-row" className="hm-btn" data-testid="hero-cta-flagship">
                  Britannia Row →
                </Link>
                <Link to="/about" className="hm-btn hm-btn-ghost" data-testid="hero-cta-about">
                  Our approach
                </Link>
              </div>
            </div>
            <div className="md:col-span-5 flex justify-center md:justify-end hm-fade-up hm-fade-up-d2">
              <div className="relative inline-block" style={{ width: "100%", maxWidth: 560 }}>
                <div className="hm-heartbeat-glow" aria-hidden="true" />
                <img
                  src={HERO_GFX}
                  alt="Har-Monic — Soulful Regeneration"
                  className="hm-heartbeat relative w-full h-auto"
                  data-testid="hero-orb"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="hm-container py-28 md:py-40">
        <div className="grid md:grid-cols-12 gap-16">
          <div className="md:col-span-4">
            <p className="hm-eyebrow">Mission</p>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display leading-[1.05] tracking-[-0.01em]" style={{ fontSize: "clamp(28px, 3.6vw, 52px)", fontWeight: 400 }}>
              Buildings carry meaning before we touch them. Our job is to keep that meaning intact while making the spaces useful for the next fifty years.
            </h2>
            <p className="mt-10 text-[color:var(--hm-ink-dim)] text-[17px] max-w-2xl leading-relaxed">
              We buy properties that others see as too old, too complicated, or too far gone. Using our decades of expertise, we turn them into places people actually want to be — homes, workspaces, and cultural spaces that champion the best of British creativity.
            </p>
          </div>
        </div>
      </section>

      {/* Flagship teaser */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={FLAGSHIP_IMG} alt="" className="w-full h-full object-cover object-center opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--hm-bg)] via-[rgba(35,27,25,0.55)] to-[rgba(35,27,25,0.35)]" />
        </div>
        <div className="relative hm-container py-36 md:py-52">
          <p className="hm-eyebrow mb-6">Flagship Project</p>
          <h3 className="font-display leading-[1.02] tracking-[-0.015em] max-w-4xl" style={{ fontSize: "clamp(36px, 5.4vw, 76px)", fontWeight: 400 }}>
            Britannia Row, Islington — the former Pink Floyd recording studios.
          </h3>
          <p className="mt-10 max-w-2xl text-[17px] text-[color:var(--hm-ink-dim)] leading-relaxed">
            A culturally significant building, brought back into use. Currently running as a meanwhile-use cultural space; plans for mixed-use redevelopment including homes and creative space are currently being considered.
          </p>

          <img
            src={BRITANNIA_LOGO}
            alt="Britannia"
            className="mt-14 max-w-[520px] w-full h-auto opacity-95"
            data-testid="britannia-logo-home"
          />

          <div className="mt-10">
            <Link to="/projects/35-britannia-row" className="hm-btn" data-testid="flagship-cta">
              Learn more about the development →
            </Link>
          </div>
        </div>
      </section>

      {/* Britannia Row — What's on teaser */}
      <section className="hm-container py-28 md:py-36" data-testid="home-events-teaser">
        <div className="grid md:grid-cols-12 gap-14 items-end mb-16">
          <div className="md:col-span-7">
            <p className="hm-eyebrow mb-6">Now on at Britannia Row</p>
            <h3
              className="font-display leading-[1.05] tracking-[-0.01em]"
              style={{ fontSize: "clamp(30px, 4vw, 54px)", fontWeight: 400 }}
            >
              A cultural programme running through 2026 — exhibitions, residencies and performances inside the building.
            </h3>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link
              to="/projects/35-britannia-row/events"
              className="hm-btn"
              data-testid="home-events-cta"
            >
              See the full programme →
            </Link>
          </div>
        </div>

        <ol className="border-t border-[color:var(--hm-line)]">
          {[
            {
              date: "24.07 — 01.08",
              tag: "Florence Trust",
              title: "FT Artists in Residency — End of Year Show",
              kind: "Residency exhibition",
            },
            {
              date: "06.08 — 22.08",
              tag: "EUCA Annex",
              title: "Duo Show 3 — Paul McDevitt & Tommy Støckel",
              kind: "Exhibition",
            },
            {
              date: "01.09 — 16.09",
              tag: "Sentient Projects",
              title: "\"How to build a Crystal Palace\" — Vattan Projects",
              kind: "Performance · gathering",
            },
          ].map((e, i) => (
            <li
              key={i}
              className="grid md:grid-cols-12 gap-8 md:gap-14 py-8 md:py-10 border-b border-[color:var(--hm-line)]"
              data-testid={`home-event-${i}`}
            >
              <div className="md:col-span-3">
                <p
                  className="font-display leading-none"
                  style={{ fontSize: "clamp(20px, 1.8vw, 26px)", fontWeight: 400 }}
                >
                  {e.date}
                </p>
                <p className="hm-eyebrow mt-3 text-[color:var(--hm-pink)]">{e.tag}</p>
              </div>
              <div className="md:col-span-9">
                <h4
                  className="font-display leading-[1.15]"
                  style={{ fontSize: "clamp(19px, 2vw, 26px)", fontWeight: 400 }}
                >
                  {e.title}
                </h4>
                <p className="mt-2 text-[13px] tracking-[0.02em] uppercase text-[color:var(--hm-ink-dim)]">
                  {e.kind}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Journal teaser removed */}
    </div>
  );
}
